import { type FunctionComponent, useEffect } from 'react';

import { Input } from '@dr.pogodin/react-utils';

import { AdminPractitionerSelect } from 'components/admin';
import { VideoIntents } from 'components/admin/VideoIntents';
import { VideoModalities } from 'components/admin/VideoModalities';

import type { ArtistT, ModalityT, VideoIntentT } from 'utils';

import IconClose from 'assets/images/icon-cross-24px.svg';

import './style.scss';

export type FiltersT = {
  customModalities?: string[];
  intents?: VideoIntentT[];
  maxDuration?: number;
  minDuration?: number;
  modalities?: ModalityT[];
  practitioner?: ArtistT;
};

export function countFilters(filters: FiltersT): number {
  let res = 0;
  if (filters.customModalities?.length || filters.modalities?.length) ++res;
  if (filters.intents?.length) ++res;
  if (filters.maxDuration !== undefined || filters.minDuration !== undefined) {
    ++res;
  }
  if (filters.practitioner) ++res;
  return res;
}

type PropsT = {
  isPractitioner?: boolean;
  onChange: (value: FiltersT) => void;
  onClose: () => void;
  value: FiltersT;
};

export const FiltersPanel: FunctionComponent<PropsT> = ({
  isPractitioner = false,
  onChange,
  onClose,
  value,
}) => {
  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  return (
    <>
      <div
        onClick={onClose}
        onKeyDown={onClose}
        role="presentation"
        styleName="backdrop"
      />
      <div styleName="modalContainer">
        <div styleName="header">
          <h2 styleName="title">Filters</h2>
          <div styleName="headerActions">
            <button
              disabled={!countFilters(value)}
              onClick={() => {
                onChange({});
              }}
              styleName="clearAllLink"
              type="button"
            >
              Clear all
            </button>
            <button
              onClick={onClose}
              styleName="closeButton"
              type="button"
            >
              <IconClose />
            </button>
          </div>
        </div>

        <div styleName="filters">
          {!isPractitioner && (
            <div styleName="filterGroup">
              <div styleName="checkboxGroup">
                <div styleName="checkboxGroupTitle">By creator</div>
                <AdminPractitionerSelect
                  emptyLabel="By creator"
                  noTransferConfirmation
                  onChange={(practitioner) => {
                    if (Array.isArray(practitioner)) return;
                    onChange({ ...value, practitioner });
                  }}
                  practitioner={value.practitioner}
                />
              </div>
            </div>
          )}

          <div styleName="filterGroup">
            <VideoIntents
              intents={value.intents ?? []}
              label="By Intent"
              onChange={(intents) => {
                onChange({ ...value, intents });
              }}
            />
          </div>

          <div styleName="filterGroup">
            <VideoModalities
              customModalities={value.customModalities ?? []}
              label="By Modality"
              modalities={value.modalities ?? []}
              onChange={(modalities, customModalities) => {
                onChange({
                  ...value,
                  customModalities,
                  modalities,
                });
              }}
            />
          </div>

          <div styleName="filterGroup">
            <div styleName="checkboxGroup">
              <div styleName="checkboxGroupTitle">By duration</div>
              <div styleName="durationInputs">
                <Input
                  onChange={(e) => {
                    onChange({
                      ...value,
                      minDuration: parseFloat(e.target.value) || undefined,
                    });
                  }}
                  placeholder="From (mins)"
                  type="number"
                  value={value.minDuration ?? ''}
                />
                <Input
                  onChange={(e) => {
                    onChange({
                      ...value,
                      maxDuration: parseFloat(e.target.value) || undefined,
                    });
                  }}
                  placeholder="Up to (mins)"
                  type="number"
                  value={value.maxDuration ?? ''}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
